import * as outlineIcons from '@heroicons/vue/24/outline';
import { OptionType } from '@/types';
import { DateTime } from 'luxon';
import CodiceFiscale from 'codice-fiscale-js';

export type IconNames = keyof typeof outlineIcons;
export type BadgeColor =
  | 'blue'
  | 'yellow'
  | 'red'
  | 'green'
  | 'gray'
  | 'pink'
  | 'lightPurple'
  | 'lightBlue'
  | 'grayBgWhiteText'
  | 'redBgWhiteText'
  | 'primaryBgWhiteText'
  | 'redBorder'
  | 'purpleBorder'
  | 'blueBorder'
  | 'greenBorder'
  | 'yellowBorder'
  | 'lightBlueBgPrimaryText'
  | 'lightGrayBgWhiteText';

export type BannerColor = 'yellow' | 'red' | 'primary' | 'gray';
export type BadgeLabel = 'Rossa Dem' | 'Rossa' | 'Bianca' | 'Bianca Dem' | 'Cronica' | 'Ordinaria' | 'RMR';
export const IMPORTED_NOTES_BUS = 'imported-notes';

export type AutocompleteOptionType = OptionType & {
  prefixIcon?: {
    name?: IconNames;
    isSolid?: boolean;
  };
  semiboldText?: string;
};

export const isAutocompleteOptionType = (o: any): o is AutocompleteOptionType => {
  return !!o.prefixIcon || !!o.semiboldText;
};

export const getEnumFromString = <T>(type: any, str: string | null): T[keyof T] | null => {
  if (!str) return null;
  const strings = str.split('_');
  let casted = '';
  strings.forEach(element => {
    casted += element[0] + element.substring(1).toLowerCase();
  });
  return type[casted];
};

export const currencyToReadableString = (n: number) => {
  const curr = n.toString();
  if (curr.length > 2) {
    return curr.substring(0, curr.length - 2) + ',' + curr.substring(curr.length - 2, curr.length);
  } else if (n === 0) {
    return '0';
  } else {
    return '0,' + curr;
  }
};
export const cleanArray = <T>(arrayObj: (T | null | undefined | Error)[] | undefined | null): T[] =>
  (arrayObj ?? []).filter((x): x is T => x !== null && x !== undefined && !(x instanceof Error));

export const downloadFileViaUrl = (url: string, filename: string) => {
  fetch(url)
    .then(response => response.blob())
    .then(blob => {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    })
    .catch(err => {
      console.error(err);
    });
};

export const getReadableDate = (d: number) => {
  if (typeof d === 'string') {
    d = parseInt(d);
  }
  if (!d) return '/';
  const inputDate = DateTime.fromSeconds(d).toLocal();
  switch (inputDate.diffNow('days').days) {
    case -1:
      return 'Ieri';
    case 0:
      return 'Oggi';
    case 1:
      return 'Domani';
    default:
      return inputDate.toLocaleString(DateTime.DATE_MED);
  }
};

export const getReadableDateTime = (d: number | string | Date, dateTimeFormatOptions?: Intl.DateTimeFormatOptions) => {
  if (!dateTimeFormatOptions) {
    dateTimeFormatOptions = DateTime.DATETIME_MED;
  }
  if (typeof d === 'string') {
    d = parseInt(d);
  } else if (d instanceof Date) {
    d = d.getTime() / 1000;
  }
  return DateTime.fromSeconds(d).toLocaleString(dateTimeFormatOptions);
};

export const getReadableTime = (
  d: number | string | Date,
  shortify?: boolean, // if true, hour like 11:00 becomes simply 11, and 09:30 becomes 9:30
): string => {
  if (typeof d === 'string') {
    d = parseInt(d);
  } else if (d instanceof Date) {
    d = d.getTime() / 1000;
  }
  if (!d) return '/';

  const dateTime = DateTime.fromSeconds(d);
  if (shortify) {
    if (dateTime.minute === 0) {
      return String(dateTime.hour);
    } else {
      const minute = String(dateTime.minute);
      return dateTime.hour + ':' + (minute.length === 1 ? '0' + minute : minute);
    }
  }
  if (shortify && dateTime.minute === 0) {
    return String(dateTime.hour);
  } else {
    return dateTime.toLocaleString(DateTime.TIME_24_SIMPLE);
  }
};

export const isValidPhoneNumber = (phoneNumber: string) => {
  const PHONE_NUMBER_REGEX = new RegExp(/^\+[1-9]\d{1,14}$/);
  return PHONE_NUMBER_REGEX.test(phoneNumber);
};

const MEF_11 = /^\d{11}$/;
const NAR = /^3\d{15}$/;
const STP = /^STP\d{13}$/;
const ENI = /^ENI\d{13}$/;

/* taxCode has to take into account other formats used by temporary IDs or similar scenarios. Specifically:
MEF16: AAAAAANNANNANNNA (normal taxCode)
MEF11: NNNNNNNNNNN
NAR: 3NNNNNNNNNNNNNNN
STP: STPNNNNNNNNNNNNN
ENI: ENINNNNNNNNNNNNN
*/

export const isValidTaxCode = (taxCode: string): boolean => {
  if (!taxCode) return true;
  return CodiceFiscale.check(taxCode) || MEF_11.test(taxCode) || NAR.test(taxCode) || STP.test(taxCode) || ENI.test(taxCode);
};

export const isValidPaiCode = (paiCode: string): boolean => {
  return /^\d{6}\.\d{9}\.\d{4}\.\d+$/.test(paiCode);
};

export const isValidDecimal = (value: string) => {
  if (!value) return true;
  return /^[0-9]+([.,][0-9]+)?$/.test(value);
};

/**
 * Parses range values from bracket format: [min][max] e.g., [-42.2][33.5]
 * @param rangeStr - The range string in bracket format
 * @returns An object with min and max values, or null if parsing fails
 */
export const parseRangeValues = (rangeStr: string): { min: number; max: number } | null => {
  const bracketMatch = rangeStr.match(/\[([^\]]+)\]\[([^\]]+)\]/);
  if (!bracketMatch) return null;

  const min = parseFloat(bracketMatch[1]);
  const max = parseFloat(bracketMatch[2]);
  if (isNaN(min) || isNaN(max)) return null;

  return { min, max };
};

export const isValidRange = (value: any, params: any[]): boolean => {
  if (value === null || value === undefined || value === '') return true;
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return false;

  const rangeStr = params[0] as string;
  const rangeValues = parseRangeValues(rangeStr);
  if (!rangeValues) return false;

  return numValue >= rangeValues.min && numValue <= rangeValues.max;
};
